import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadIcon, CheckCircledIcon, CircleIcon } from '@radix-ui/react-icons';
import { cn } from '@/shared/ui/utils/tailwind';
import { Button, Text, Label, NativeFileInput } from '@/shared/ui';
import { DEAL_PANEL_CLASS, DealPanelLabel } from './DealPanel';
import { DealsService } from '@/services/requests/housien-services/DealsService';
import {
  SALE_CLOSING_CHECKLIST,
  RENTAL_CLOSING_CHECKLIST,
  type ClosingChecklistItem,
} from '../constants/closing-checklist';

interface DealClosingPanelProps {
  agencyId: string;
  dealId: string;
  dealType: 'sale' | 'rental';
  initialChecklist: Record<string, boolean> | null | undefined;
  onUpload: (file: File) => Promise<void>;
  /**
   * Reports the new checklist after every toggle, so the counts the
   * detail screen shows elsewhere (the CHECKLIST tile, the tab badge)
   * move with the boxes instead of waiting for a reload.
   */
  onChecklistChange?: (checklist: Record<string, boolean>) => void;
}

/**
 * Builds the initial checked set from the deal's persisted checklist.
 */
function checkedIdsFrom(checklist: Record<string, boolean> | null | undefined): Set<string> {
  return new Set(Object.entries(checklist ?? {}).filter(([, done]) => done).map(([id]) => id));
}

/**
 * Checklist panel for the closing stage. Each item can be marked done,
 * and items that require a document show an upload action. Progress
 * persists via PUT (closing_checklist) [Plano 3, Fase 4, SC-5] -- it
 * comes from the deal, not local-only state that a reload would lose.
 *
 * **It does not gate the win, by decision (D6, 02/09/2026).** The list
 * mixes commercial closure with paperwork that lands weeks later:
 * `sale_state.py` records escritura, ITBI and the RI registration as
 * dates precisely because they "can land out of order and sometimes
 * weeks apart". Requiring `deed_registered` before the win would keep a
 * commercially closed deal open in the funnel for weeks and push the
 * broker to tick boxes they had not done. The paperwork has its own
 * home: Sale opens at `in_paperwork` and Lease at `draft`.
 *
 * This panel therefore reports, and never blocks.
 *
 * @param props.agencyId - The owning agency id.
 * @param props.dealId - The deal being closed.
 * @param props.dealType - sale or rental, determines the checklist.
 * @param props.initialChecklist - The deal's persisted checklist, if any.
 * @param props.onUpload - Callback to upload a file.
 * @returns A checklist panel.
 */
export function DealClosingPanel({
  agencyId,
  dealId,
  dealType,
  initialChecklist,
  onUpload,
  onChecklistChange,
}: DealClosingPanelProps) {
  const { t } = useTranslation('features/deals');
  const items: ClosingChecklistItem[] =
    dealType === 'sale' ? SALE_CLOSING_CHECKLIST : RENTAL_CLOSING_CHECKLIST;

  const [checked, setChecked] = useState<Set<string>>(() => checkedIdsFrom(initialChecklist));

  // Counted item by item against the current deal type's list, not by
  // the size of the checked set: a deal switched between sale and rental
  // keeps the other list's ids, and cardinality alone read those as
  // progress -- five stale sale ids showed "5/5" on a rental with
  // nothing done.
  const doneCount = items.filter((item) => checked.has(item.id)).length;

  /**
   * Persists the given checked-id set as the deal's closing_checklist.
   */
  function persist(nextChecked: Set<string>) {
    const checklist = Object.fromEntries(items.map((item) => [item.id, nextChecked.has(item.id)]));
    onChecklistChange?.(checklist);
    void DealsService.updateDeal(agencyId, dealId, { closing_checklist: checklist });
  }

  /**
   * Toggles a checklist item between done and not-done.
   */
  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      persist(next);
      return next;
    });
  }

  /**
   * Handles file selection from the hidden file input.
   */
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>, itemId: string) {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file).then(() => {
        setChecked((prev) => {
          const next = new Set(prev);
          next.add(itemId);
          persist(next);
          return next;
        });
      });
    }
  }

  return (
    <div className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')}>
      <DealPanelLabel
        accessory={(
          <Text
            component="span"
            data-testid="deal-checklist-count"
            className="whitespace-nowrap text-[length:var(--text-meta)] [font-weight:var(--font-bold)] tabular-nums text-(--seed-base)"
          >
            {t('detail.summary.checklistCount', { done: doneCount, total: items.length })}
          </Text>
        )}
      >
        {t('detail.closing.title')}
      </DealPanelLabel>

      {/* The count says how far along; the bar says it at a glance, which
          is what a reader scanning the tab strip's badge came here for. */}
      <div className="h-[6px] overflow-hidden rounded-[3px] bg-(--surface-tertiary)">
        <div
          className="h-full rounded-[3px] bg-(--seed-base)"
          style={{ width: `${items.length === 0 ? 0 : Math.round((doneCount / items.length) * 100)}%` }}
        />
      </div>

      <div role="list" className="flex flex-col gap-[var(--space-2)]">
        {items.map((item) => {
          const isDone = checked.has(item.id);
          return (
            <div
              role="listitem"
              key={item.id}
              className="flex items-center justify-between gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-(--surface-primary) px-(--space-3) py-(--space-2)"
            >
              <Button
                variant="ghost"
                onClick={() => toggle(item.id)}
                className={cn(
                  'flex items-center gap-[var(--space-2)] bg-transparent border-none cursor-pointer p-0 text-sm',
                  isDone ? 'text-[var(--status-success-text)]' : 'text-[var(--text-primary)]',
                )}
              >
                {isDone
                  ? <CheckCircledIcon width={16} height={16} />
                  : <CircleIcon width={16} height={16} />}
                <Text component="span" className={isDone ? 'line-through' : ''}>
                  {t(item.labelKey)}
                </Text>
              </Button>

              {item.requiresDocument && !isDone && (
                <Button
                  variant="ghost"
                  className="flex items-center gap-[var(--space-1)] cursor-pointer text-xs text-[var(--seed-base)] hover:underline p-0"
                >
                  <Label>
                    <UploadIcon width={12} height={12} />
                    <NativeFileInput
                      className="hidden"
                      onChange={(e) => handleFileChange(e, item.id)}
                    />
                  </Label>
                </Button>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}
